import { Button } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
} from '../../../../../components/drawer/drawer';
import {
  MultiSelect,
  SelectableOption,
} from '../../../../../components/input/multi-select';
import { ActualsSetFilter, SetSelect } from '../../../set-select';
import { ExerciseOption } from '../../../types';
import { getUniqueExerciseOptions } from '../../../utils';

export const FilterDrawer = ({
  open,
  exerciseOptionFilters,
  setExerciseOptionFilters,
  exerciseOptions,
  setFilter,
  setSetFilter,
  onClose,
}: {
  open: boolean;
  exerciseOptionFilters: SelectableOption[];
  setExerciseOptionFilters: (options: SelectableOption[]) => void;
  exerciseOptions: ExerciseOption[];
  setFilter: ActualsSetFilter;
  setSetFilter: (filter: ActualsSetFilter) => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const [selectedExerciseOptions, setSelectedExerciseOptions] = useState<
    SelectableOption[]
  >(exerciseOptionFilters);
  const [selectedSetFilter, setSelectedSetFilter] =
    useState<ActualsSetFilter>(setFilter);

  // Sync internal state when props change (e.g., when filters are cleaned up)
  useEffect(() => {
    setSelectedExerciseOptions(exerciseOptionFilters);
  }, [exerciseOptionFilters]);

  useEffect(() => {
    setSelectedSetFilter(setFilter);
  }, [setFilter]);

  const handleExerciseSelection = (options: SelectableOption[]) => {
    const wasAllSelected = selectedExerciseOptions.length === 0;
    const isAllSelected = options.length === 0;
    if (!wasAllSelected && isAllSelected) {
      setSelectedExerciseOptions([]);
    } else {
      setSelectedExerciseOptions(options);
    }
  };

  const resetToAppliedFilters = () => {
    setSelectedExerciseOptions(exerciseOptionFilters);
    setSelectedSetFilter(setFilter);
  };

  const handleClose = () => {
    resetToAppliedFilters();
    onClose();
  };

  const uniqueExerciseOptions = useMemo(
    () => getUniqueExerciseOptions(exerciseOptions),
    [exerciseOptions],
  );

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      anchor="right"
      sx={{
        '& .MuiPaper-root': {
          minWidth: 400,
        },
      }}
    >
      <DrawerHeader title={t('filterExercises')} onClose={handleClose} />
      <div className="flex flex-col gap-2 mx-6 overflow-y-hidden grow">
        <MultiSelect
          label={t('exercises')}
          selectedOptions={selectedExerciseOptions}
          options={uniqueExerciseOptions}
          onChange={handleExerciseSelection}
          optionToName={(e) => e.name}
          placeholder={t('allExercises')}
        />
        <SetSelect
          filter={selectedSetFilter}
          setFilter={setSelectedSetFilter}
        />
      </div>

      <DrawerFooter className="flex flex-row gap-2 w-full p-8 justify-end">
        <Button onClick={handleClose} variant="outlined">
          {t('cancel')}
        </Button>
        <Button
          onClick={() => {
            setExerciseOptionFilters(selectedExerciseOptions);
            setSetFilter(selectedSetFilter);
            handleClose();
          }}
        >
          {t('applyFilters')}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};
